export type ComplaintReason =
  | 'missing_product'
  | 'bad_condition'
  | 'delay'
  | 'wrong_item'
  | 'other'
  | 'delivery_delay'
  | 'never_arrived'
  | 'damaged_in_transit'
  | 'rider_behavior'
  | 'service_quality'
  | 'professional_late'
  | 'no_show_merchant'
  | 'billing';

export type ComplaintStatus =
  | 'open'
  | 'pending_customer'
  | 'resolved'
  | 'escalated';

export type AllegedParty = 'merchant' | 'rider' | 'both' | 'unknown';

export const COMPLAINT_REASON_LABELS: Record<ComplaintReason, string> = {
  missing_product: 'Producto faltante',
  bad_condition: 'Mal estado',
  delay: 'Demora',
  wrong_item: 'Producto equivocado',
  other: 'Otro',
  delivery_delay: 'Demora en la entrega',
  never_arrived: 'Nunca llegó',
  damaged_in_transit: 'Dañado en tránsito',
  rider_behavior: 'Conducta del repartidor',
  service_quality: 'Calidad del servicio',
  professional_late: 'Llegó tarde / demora del profesional',
  no_show_merchant: 'El local no atendió',
  billing: 'Cobro / facturación',
};

/**
 * El CHECK del server conserva `delay` (retail/pickup) y `delivery_delay`
 * (delivery) como motivos distintos, pero para el comercio son el mismo:
 * "llegó tarde". Consolidación solo de display — sin migración de datos.
 * (UX audit #1467)
 */
const REASON_DISPLAY_ALIAS: Partial<Record<ComplaintReason, ComplaintReason>> = {
  delivery_delay: 'delay',
};

/** Motivo canónico para mostrar/filtrar (consolida near-duplicados). */
export function canonicalComplaintReason(reason: ComplaintReason): ComplaintReason {
  return REASON_DISPLAY_ALIAS[reason] ?? reason;
}

/** Label user-facing del motivo, ya consolidado ('delivery_delay' → 'Demora'). */
export function complaintReasonLabel(reason: ComplaintReason): string {
  return COMPLAINT_REASON_LABELS[canonicalComplaintReason(reason)] ?? reason;
}

/**
 * Opciones de filtro de motivo sin duplicados de display:
 * una entrada por motivo canónico (vale para chips o dropdown).
 */
export function complaintReasonFilterOptions(): { key: ComplaintReason; label: string }[] {
  const seen = new Set<ComplaintReason>();
  const options: { key: ComplaintReason; label: string }[] = [];
  for (const key of Object.keys(COMPLAINT_REASON_LABELS) as ComplaintReason[]) {
    const canonical = canonicalComplaintReason(key);
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    options.push({ key: canonical, label: COMPLAINT_REASON_LABELS[canonical] });
  }
  return options;
}

export const ALLEGED_PARTY_LABELS: Record<AllegedParty, string> = {
  merchant: 'Local',
  rider: 'Repartidor',
  both: 'Ambos',
  unknown: 'No está seguro',
};

export const COMPLAINT_STATUS_LABELS: Record<ComplaintStatus, string> = {
  open: 'Abierto',
  pending_customer: 'Esperando al cliente',
  resolved: 'Resuelto',
  escalated: 'Escalado',
};

export const COMPLAINT_STATUS_COLORS: Record<ComplaintStatus, string> = {
  open: '#0F766E',
  pending_customer: '#3DD68C',
  resolved: '#9AA3BD',
  escalated: '#FF6B6B',
};

export type ComplaintRow = {
  id: string;
  order_id: string | null;
  appointment_id: string | null;
  reason: ComplaintReason;
  description: string;
  photo_urls: string[];
  status: ComplaintStatus;
  alleged_party: AllegedParty;
  kiosco_response: string | null;
  kiosco_responded_at: string | null;
  deadline_at: string;
  escalated_at: string | null;
  resolved_at: string | null;
  created_at: string;
  customer_name: string;
  customer_phone: string | null;
  order_total: number;
  order_status: string;
  /** Short store-facing order code when subject is an order. */
  display_code: string | null;
  pickup_type: string | null;
  subject_kind: 'order' | 'appointment';
  appointment_label: string | null;
  response_revision_count?: number;
  /** Deadline for the customer to decide after merchant response (48h from response). */
  customer_decision_deadline_at?: string | null;
};

export type ComplaintOrderItem = {
  product_name: string;
  quantity: number;
  unit_price: number;
  options?: Array<{ choice_name?: string; choiceName?: string; qty?: number }> | null;
  line_note?: string | null;
};

export type ComplaintDetail = ComplaintRow & {
  items: ComplaintOrderItem[];
};

export type ComplaintHistoryEntry = {
  id: string;
  event_type: 'created' | 'status_change' | 'merchant_responded' | 'customer_decided';
  from_status: string | null;
  to_status: string | null;
  actor_kind: 'merchant' | 'fiado' | 'system' | 'ops';
  metadata: Record<string, unknown>;
  created_at: string;
};

export const HISTORY_EVENT_LABELS: Record<ComplaintHistoryEntry['event_type'], string> = {
  created: 'Reclamo creado',
  status_change: 'Cambio de estado',
  merchant_responded: 'Respuesta del local',
  customer_decided: 'Decisión del cliente',
};

export const ACTOR_KIND_LABELS: Record<ComplaintHistoryEntry['actor_kind'], string> = {
  merchant: 'Local',
  fiado: 'Cliente',
  system: 'Sistema',
  ops: 'Vuelto',
};

export function formatComplaintItemOptionsLabel(
  options: ComplaintOrderItem['options'],
): string {
  if (!options?.length) return '';
  return options
    .map((o) => {
      const name = String(o.choiceName ?? o.choice_name ?? '').trim();
      const qty = Math.max(1, Number(o.qty) || 1);
      if (!name) return '';
      return qty > 1 ? `${name}×${qty}` : name;
    })
    .filter(Boolean)
    .join(', ');
}

export type ComplaintStats = {
  total: number;
  resolved: number;
  escalated: number;
  byReason: Record<ComplaintReason, number>;
};

export function hoursUntilDeadline(deadlineAt: string): number {
  const now = Date.now();
  const deadline = new Date(deadlineAt).getTime();
  return Math.round((deadline - now) / (1000 * 60 * 60));
}

/** Human-readable deadline label: "Vence en 5h" or "Venció hace 3h" when overdue. */
export function formatDeadlineLabel(deadlineAt: string): string {
  const hours = hoursUntilDeadline(deadlineAt);
  if (hours < 0) return `Venció hace ${Math.abs(hours)}h`;
  return `Vence en ${hours}h`;
}

export function emptyReasonStats(): Record<ComplaintReason, number> {
  return {
    missing_product: 0,
    bad_condition: 0,
    delay: 0,
    wrong_item: 0,
    other: 0,
    delivery_delay: 0,
    never_arrived: 0,
    damaged_in_transit: 0,
    rider_behavior: 0,
    service_quality: 0,
    professional_late: 0,
    no_show_merchant: 0,
    billing: 0,
  };
}
